import {err, ok, ValidationError} from 'framework';
import type {Result, Validator} from 'framework';
import * as z from 'zod';
import type {CreateNutritionPlanInput} from '../ports/nutrition-plan.repository';
import {isLocalDate} from '../../lib/local-date';

const KCAL_MAX_EXCLUSIVE = 20000;
const MACRO_MAX = 2000;
const PLAN_NAME_MAX = 80;

const planDateSchema = z
  .string()
  .refine(isLocalDate, {message: 'Date must be formatted as YYYY-MM-DD.'})
  .nullable();

const nutritionTargetsSchema = z
  .strictObject({
    kcal: z.number().positive().lt(KCAL_MAX_EXCLUSIVE).optional(),
    proteinGrams: z.number().min(0).max(MACRO_MAX).optional(),
    carbsGrams: z.number().min(0).max(MACRO_MAX).optional(),
    fatGrams: z.number().min(0).max(MACRO_MAX).optional(),
  })
  .refine(
    targets =>
      targets.kcal !== undefined ||
      targets.proteinGrams !== undefined ||
      targets.carbsGrams !== undefined ||
      targets.fatGrams !== undefined,
    {message: 'Set at least one target (calories, protein, carbs, or fat).'},
  );

export class CreateNutritionPlanDataValidator implements Validator<CreateNutritionPlanInput> {
  readonly standardSchema = z
    .strictObject({
      name: z.string().trim().min(1, 'Name is required.').max(PLAN_NAME_MAX),
      targets: nutritionTargetsSchema,
      startDate: planDateSchema,
      endDate: planDateSchema,
    })
    .superRefine((value, ctx) => {
      if (value.startDate !== null && value.endDate !== null && value.startDate > value.endDate) {
        ctx.addIssue({
          code: 'custom',
          message: 'End date must be on or after start date.',
          path: ['endDate'],
        });
      }
    });

  validate(value: unknown): Result<CreateNutritionPlanInput, ValidationError> {
    const parsed = this.standardSchema.safeParse(value);

    if (!parsed.success) {
      return err(
        new ValidationError(
          parsed.error.issues[0]?.message ?? 'Invalid nutrition plan create data.',
        ),
      );
    }

    return ok(parsed.data);
  }
}
